import React, { useState, useEffect } from 'react';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Box } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Button } from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import { useInventoryCategories } from '../../../hooks/inventory/useInventoryCategories';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useTranslation } from 'react-i18next';
import { truncateString } from '../../../utils/truncateString';
import LoadingSpinner from '../../common/controls/LoadingSpinner';
import InventoryCategoryForm from '../forms/InventoryCategoryForm';
import { useDeleteInventoryCategory } from '../../../hooks/inventory/useDeleteInventoryCategory';
import { useSnackbar } from '../../../context/SnackbarContext';
import DefaultCategoriesSeederDialog from './DefaultCategoriesSeederDialog';
import RequirePermission from '../../RequirePermission';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';

export default function InventoryCategories() {

    const { t } = useTranslation('common');
    const { showSnackbar } = useSnackbar();

    const [open, setOpen] = React.useState(false);
    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
    };
    const [listTrigger, setListTrigger] = React.useState(false);
    const toggleListTrigger = () => {
        setListTrigger(prev => !prev);
    };

    const onEmptyData = () => {
        setSeederDialogOpen(true);
    };

    const { inventoryCategories, loading, setInventoryCategories } = useInventoryCategories(listTrigger, onEmptyData);

    const { remove } = useDeleteInventoryCategory(toggleListTrigger);

    const [selectedInventoryCategory, setSelectedInventoryCategory] = useState();

    const [formOpen, setFormOpen] = React.useState(false);
    const [formMode, setFormMode] = React.useState("create");
    const [seederDialogOpen, setSeederDialogOpen] =  React.useState(false);

    const handleAdd = () => {
        setSelectedInventoryCategory(null);
        setFormMode("create");
        setFormOpen(true);
    };

    const handleEdit = (inventoryCategory) => {
        setSelectedInventoryCategory(inventoryCategory);
        setFormMode("edit");
        setFormOpen(true);
    };

    const onSaveSuccess = () => {
        toggleListTrigger();
        setFormOpen(false);
    };

    const handleDelete = async (inventoryCategory) => {
        try {
            await remove(inventoryCategory.id);
            showSnackbar(t('inventory.categories.messages.delete_success', { name: inventoryCategory.name }), "success");
        } catch (err) {
            if (err.status === 409)
            {
                showSnackbar(t('inventory.categories.messages.delete_fail_still_assigned', { name: inventoryCategory.name }), "error");
            } else {
                showSnackbar(t('inventory.categories.messages.delete_fail', { name: inventoryCategory.name }), "error");
            }
        }
    };

    return (
        <>
        <ListItem>
            <ListItemButton onClick={handleClickOpen}>
            <ListItemIcon>
                <CategoryIcon fontSize='large' color='secondary' />
            </ListItemIcon>
            <ListItemText 
                primary="Categories"
                secondary="view and manage inventory categories"
            />
            </ListItemButton>
        </ListItem>

        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
            <DialogTitle>
                Inventory Categories
            </DialogTitle>
            <DialogContent>
                <List>
                {loading ? (
                    <ListItem>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <LoadingSpinner />
                    </Box>
                    </ListItem>
                ) : (
                    inventoryCategories?.map((cat) => (
                    <ListItem
                        key={cat.id}
                        secondaryAction={
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <VerticalHamburgerMenu
                            options={[
                                { 
                                id: 1, 
                                name: 'Edit',
                                callback: () => handleEdit(cat),
                                disabled: !userHasAccess(SUB_MODULES.CATEGORIES, 2)
                                },
                                {
                                id: 2,
                                name: 'Delete',
                                callback: () => handleDelete(cat),
                                confirmMessage: t('inventory.categories.messages.delete_confirm', { name: cat.name }),
                                disabled: !userHasAccess(SUB_MODULES.CATEGORIES, 3)
                                },
                            ]}
                            />
                        </Box>
                        }
                    >
                        <ListItemButton onClick={() => {if (userHasAccess(SUB_MODULES.CATEGORIES, 2)) handleEdit(cat)}}>
                        <ListItemText
                            primary={cat.name}
                            secondary={
                            <Typography variant="body2" sx={{ maxWidth: '90%' }}>
                                {truncateString(cat.description, 200)}
                            </Typography>
                            }
                        />
                        </ListItemButton>
                    </ListItem>
                    ))
                )}
                </List>
            </DialogContent>
            <DialogActions>
              <RequirePermission subModuleName={SUB_MODULES.CATEGORIES} minAccessLevel={2}>
                <Button onClick={handleAdd}>Add Category</Button>
              </RequirePermission>
              <div style={{flex: '1 0 0'}} />
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
        <InventoryCategoryForm 
            successCallback={onSaveSuccess}
            inventoryCategory={selectedInventoryCategory}
            mode={formMode}
            open={formOpen}
            onClose={() => setFormOpen(false)}
        />
        <DefaultCategoriesSeederDialog
            open={seederDialogOpen}
            setOpen={setSeederDialogOpen} 
            successCallback={toggleListTrigger}
        />
        </>
    );
}